// Static specialty + competitor data — bundled into frontend for instant render.
// Mirrors RESOURCE_ARTICLES / COMPETITOR_DATA / SPECIALTY_LANDING_COPY / SPECIALIZATIONS in /app/backend/routes/platform_seo.py and /app/backend/config.py.
// Keep in sync when adding/editing.

export const SPECIALIZATIONS = [
  { slug: 'cardiology', name: 'Cardiology' },
  { slug: 'dermatology', name: 'Dermatology' },
  { slug: 'ent', name: 'ENT (Otolaryngology)' },
  { slug: 'gastroenterology', name: 'Gastroenterology' },
  { slug: 'physician', name: 'Physician (MD Medicine)' },
  { slug: 'general_surgery', name: 'General Surgery' },
  { slug: 'gynecology', name: 'Gynecology & Obstetrics' },
  { slug: 'neurology', name: 'Neurology' },
  { slug: 'neurosurgery', name: 'Neurosurgery' },
  { slug: 'oncology', name: 'Oncology' },
  { slug: 'ophthalmology', name: 'Ophthalmology' },
  { slug: 'orthopedics', name: 'Orthopedics' },
  { slug: 'pediatrics', name: 'Pediatrics' },
  { slug: 'psychiatry', name: 'Psychiatry' },
  { slug: 'pulmonology', name: 'Pulmonology' },
  { slug: 'urology', name: 'Urology' },
  { slug: 'dentistry', name: 'Dentistry' },
  { slug: 'endocrinology', name: 'Endocrinology' },
  { slug: 'nephrology', name: 'Nephrology' },
  { slug: 'rheumatology', name: 'Rheumatology' },
  { slug: 'plastic_surgery', name: 'Plastic & Cosmetic Surgery' },
  { slug: 'allergy_immunology', name: 'Allergy & Immunology' },
  { slug: 'radiology', name: 'Radiology' },
  { slug: 'anesthesiology', name: 'Anesthesiology' },
  { slug: 'physiotherapy', name: 'Physiotherapy' },
  { slug: 'diabetology', name: 'Diabetology' },
  { slug: 'infectious_disease', name: 'Infectious Disease' },
  { slug: 'vascular_surgery', name: 'Vascular Surgery' },
  { slug: 'pain_management', name: 'Pain Management' },
];

// Legacy slugs that should resolve to a current canonical slug.
// Used by ForSpecialtyPage / FindDoctorsPage to 301-redirect old URLs.
export const LEGACY_SPECIALTY_REDIRECTS = {
  general_medicine: 'physician',
  internal_medicine: 'physician',
};
const SPEC_MAP = Object.fromEntries(SPECIALIZATIONS.map(s => [s.slug, s.name]));

export const SPECIALTY_LANDING_COPY = {
  cardiology: {
    hook: 'The Heart of Your Practice, Online.',
    pain_points: [
      'Patients search \'best cardiologist near me\' — but your website isn\'t on page 1.',
      'Aggregators list you alongside 50 other cardiologists — no way to stand out.',
      'You can\'t showcase your Echo Lab, Cath Lab, or Holter capability in a directory listing.',
    ],
    wins: [
      'Your own website where you showcase every cardiac procedure in detail',
      'Direct WhatsApp bookings — no aggregator commission on angioplasty cases',
      'Schema.org Physician + MedicalProcedure markup so Google shows your Echo page as a rich result',
    ],
  },
  dentistry: {
    hook: 'Your Dental Practice Deserves More Than a Listing.',
    pain_points: [
      'Aggregators charge 15-20% per root canal or implant booking.',
      'Your website (if you have one) isn\'t ranking for \'dentist near me\' in your city.',
      'Patients can\'t see your invisalign/implant portfolio before booking.',
    ],
    wins: [
      'Dedicated pages for every procedure — root canals, implants, aligners, whitening',
      'Before/after galleries with patient consent — WhatsApp-style, 10-line stories',
      '0% commission on bookings — your phone buzzes directly',
    ],
  },
  ent: {
    hook: 'The Specialist Patients Reach First — Then You Choose the Right Hospital.',
    pain_points: [
      'Patients Google \'FESS surgery in {city}\' and reach a hospital marketing page first — bypassing the ENT specialist who should actually evaluate the case.',
      'Pediatric ENT queries (\'my child\'s ear infection\') go straight to pharma blogs — not to the ENT surgeon parents need.',
      'Complex cases like cochlear implants and microlaryngoscopy need specific hospital infrastructure — but the patient\'s journey should start with the specialist who decides which centre fits this case best, not with the hospital\'s SEO team.',
    ],
    wins: [
      'Per-procedure pages — Septoplasty, FESS, Tonsillectomy — each ranks independently',
      'AI-generated patient-friendly explainers in your voice, approved by you',
      'Direct WhatsApp to your staff for emergency bookings (pediatric ENT wins)',
    ],
  },
  gynecology: {
    hook: 'Trusted Women\'s Health Starts With a Direct Specialist Conversation.',
    pain_points: [
      'Women search \'lady gynecologist near me\' — need to feel safe, not see aggregator ads.',
      'You can\'t showcase your approach on obs/gyn in a third-party directory.',
      'Privacy concerns make patients avoid aggregator phone numbers.',
    ],
    wins: [
      'Your brand, your tone — patient testimonials in your voice, not an aggregator template',
      'Direct bookings with privacy guarantee — data stays on your website',
      'Per-procedure trust content: IUDs, colposcopy, hysteroscopy, delivery options',
    ],
  },
  dermatology: {
    hook: 'Showcase Your Skin Expertise, Not Just a Listing.',
    pain_points: [
      'Aesthetic clinics flood Google Ads — patients can\'t tell board-certified from fly-by-night.',
      'You can\'t show before/after results for PRP, chemical peels, laser on a directory.',
      'Acne patients Google \'best skin doctor {city}\' and see paid listings — not you.',
    ],
    wins: [
      'Rich treatment galleries with patient consent',
      'Per-procedure pages for chemical peels, laser resurfacing, PRP hair',
      'Google rich results for before/after with MedicalProcedure schema',
    ],
  },
  orthopedics: {
    hook: 'The Surgeon Patients Talk to First — Before Any Hospital Lands the Case.',
    pain_points: [
      'Knee-pain patients search \'best orthopaedic doctor in {city}\' — and the conversation starts with a hospital marketing page or aggregator, not with the surgeon who\'ll actually examine them.',
      'You can\'t showcase arthroscopy, joint replacement, or sports-injury expertise inside a directory tile.',
      'Post-op patients can\'t easily share your name because your only online presence is a third-party profile — so referrals leak to whoever\'s ad budget is biggest that week.',
    ],
    wins: [
      'Per-procedure pages for TKR, THR, arthroscopy, spine surgery — each ranks independently',
      'Patient-friendly pre-op and post-op explainers you approve once and reuse',
      'Direct WhatsApp bookings — zero commission on major surgeries',
    ],
  },
  pediatrics: {
    hook: 'Parents Trust You. Make It Easy for Them to Find You.',
    pain_points: [
      'Parents search \'paediatrician near me\' at 2 AM with a feverish child — they click the first trustworthy-looking site.',
      'Aggregator listings show paid placements first, not the paediatrician the mother\'s friend recommended.',
      'Vaccination schedules, growth tracking, and specialty clinics can\'t be shown as structured content on a directory.',
    ],
    wins: [
      'A reassuring home page in your voice — not an aggregator template',
      'Dedicated pages for well-baby clinics, vaccination schedules, nutrition counselling',
      'WhatsApp CTA with pre-filled message: \'My child has a fever, can I book today?\'',
    ],
  },
  ophthalmology: {
    hook: 'Cataract, LASIK, Retina — Each Deserves Its Own Page.',
    pain_points: [
      'LASIK clinics dominate Google Ads; solo ophthalmologists get buried under chain-clinic advertising.',
      'Retina, glaucoma, paediatric ophthalmology can\'t be differentiated inside a directory listing.',
      'Patients can\'t see your equipment (phaco machine, OCT, fundus camera) on an aggregator profile.',
    ],
    wins: [
      'Per-procedure pages for cataract (phaco), LASIK, glaucoma surgery, retina, squint',
      'Equipment & credentials showcase on your website — instant trust signal',
      'Schema.org Physician + MedicalProcedure JSON-LD so rich results appear in Google',
    ],
  },
  general_surgery: {
    hook: 'Patients Should Reach the Surgeon First — Then Choose the Hospital Together.',
    pain_points: [
      'When a patient Googles \'hernia surgery in {city}\', a hospital landing page often shows up first — the specialist who\'ll actually perform the surgery is invisible until after the patient has already chosen the hospital.',
      'You can\'t explain your laparoscopic vs open approach or your day-care protocol on a listing.',
      'Referring physicians share aggregator URLs; the patient lands on someone else\'s profile, not on the surgeon\'s own clinical reasoning.',
    ],
    wins: [
      'Shareable per-procedure URLs (laparoscopic cholecystectomy, hernia mesh, appendix) for every referring physician',
      'Patient stories with consent — builds trust faster than any directory review',
      'Your own booking flow with day-care package clearly shown — no calls needed',
    ],
  },
  physician: {
    hook: 'The Physician Patients Reach First — Then You Decide Which Specialist or Hospital They Need.',
    pain_points: [
      'A patient with diabetes plus hypertension plus thyroid plus an unexplained anaemia walks into 500 \'physician near me\' aggregator listings — no way to find the MD Medicine consultant who can actually orchestrate care across these conditions.',
      'Post-discharge continuity falls apart when the patient\'s only digital record of you is a directory tile — the cardiologist, the surgeon, and the hospital all have stronger online surfaces than the physician who coordinates the whole case.',
      'Long-term diabetes, hypertension, thyroid, and post-COVID follow-up relationships compound over 10–20 years — but a 60-character profile on someone else\'s site can\'t carry that depth.',
    ],
    wins: [
      'A specialist-led platform surface that names you as the orchestrator — the physician who routes patients to the right sub-specialist, the right hospital, the right diagnostic workup',
      'Per-condition continuity pages: hypertension, type-2 diabetes, thyroid, dyslipidaemia, anaemia work-up, post-discharge follow-up — each one a place patients return to between visits',
      'Direct WhatsApp continuity for medication queries, lab-report doubts, and chronic-care follow-up — the long relationship aggregators can\'t represent and hospitals can\'t maintain',
    ],
  },
  gastroenterology: {
    hook: 'Endoscopy, Colonoscopy, GI Care — Your Clinic, Your Pages.',
    pain_points: [
      'Patients with acid reflux, piles, or IBS Google symptoms — pharma blogs rank, not local GI specialists.',
      'You can\'t explain your endoscopy setup, anaesthesia options, or day-care protocols inside a listing.',
      'Screening colonoscopy awareness content (a huge public-health need) isn\'t your aggregator\'s priority — it\'s yours.',
    ],
    wins: [
      'Educational pages for upper GI endoscopy, colonoscopy, piles treatment, liver care',
      'Pre-procedure preparation instructions patients can read at home',
      'Direct WhatsApp bookings for emergencies (upper GI bleeding, severe abdominal pain)',
    ],
  },
  neurology: {
    hook: 'Complex Conditions Deserve a Dedicated Specialist Page.',
    pain_points: [
      'Patients with epilepsy, Parkinson\'s, migraine, stroke often come via referral — and land on an aggregator listing, not your site.',
      'EEG, EMG, nerve conduction, and botulinum toxin therapy can\'t be showcased on a directory.',
      'Patient education (which your clinic actually delivers) is absent from aggregator profiles.',
    ],
    wins: [
      'Per-condition pages (epilepsy, Parkinson\'s, migraine, stroke rehab) that patients bookmark and return to',
      'EEG/EMG service pages with pre-test preparation and what-to-expect',
      'Shareable URLs that referring physicians and family members use — each becomes a backlink over time',
    ],
  },
  psychiatry: {
    hook: 'Mental Health Care, Without the Aggregator Feel.',
    pain_points: [
      'Anxiety and depression patients need a warm, trustworthy first impression — not an aggregator listing with ads.',
      'Confidentiality concerns make patients avoid third-party aggregator booking flows.',
      'Specialty areas (OCD, bipolar, addiction, child psychiatry) can\'t be communicated through a profile card.',
    ],
    wins: [
      'A calm, private site in your tone — designed to be the first step patients feel safe taking',
      'Confidential direct bookings — no aggregator, no third-party review feed',
      'Condition-focused pages (anxiety, depression, ADHD, OCD) that patients can send to their family for understanding',
    ],
  },
  urology: {
    hook: 'The Urologist Patients Reach First — Then You Pick the Right Surgical Centre.',
    pain_points: [
      'Kidney-stone patients Google symptoms and land on generic pharma blogs — not the urologist who actually performs PCNL and decides whether the case needs a tertiary OT or a day-care setup.',
      'Prostate-surgery queries (TURP, HoLEP) reach hospital marketing pages before the urologist who will actually perform the procedure — but the patient\'s first call should be the specialist, who then guides them to the right surgical centre.',
      'Sensitive men\'s-health queries (ED, fertility) deserve a confidential, specialist-led conversation aggregators don\'t offer.',
    ],
    wins: [
      'Dedicated pages for stone treatment (PCNL, URS, ESWL), prostate surgery, andrology',
      'Confidential WhatsApp booking for sensitive consultations',
      'Trust-building credentials and case studies — under YOUR slug, indexed under YOUR brand',
    ],
  },
  plastic_surgery: {
    hook: 'Portfolio Work Belongs on Your Own Site, Not an Aggregator\'s.',
    pain_points: [
      'Aesthetic patients research extensively before choosing a surgeon — aggregator profiles don\'t support that depth.',
      'Before/after photography (with consent) is central to cosmetic decisions — directories limit image uploads.',
      'Aggregator reviews get mixed with cosmetic chains, making it hard to stand out as a qualified plastic surgeon.',
    ],
    wins: [
      'Before/after galleries per procedure (rhinoplasty, breast aug, liposuction) — with signed patient consent',
      'Credentials & fellowship badges prominently displayed — not buried in a sidebar',
      'WhatsApp consultations with auto-sent pre-consult questionnaires',
    ],
  },
  neurosurgery: {
    hook: 'The Neurosurgeon Patients Talk to First — Then You Choose the Right Centre Together.',
    pain_points: [
      'Patients search \'best neurosurgeon in {city}\' for spine, tumour, aneurysm — aggregators dump you into one generic \'neurosurgery\' bucket, and hospital marketing pages claim the case before the surgeon ever speaks with the patient.',
      'Referring neurologists and GPs want to share a URL that explains your specific sub-speciality — a directory profile can\'t do that.',
      'Complex cases like aneurysm clipping, coiling, and stereotactic radiosurgery need specific hospital infrastructure — but the surgeon, not a hospital marketing page, should be the one deciding which centre fits this patient\'s case.',
    ],
    wins: [
      'Dedicated pages for spine (discectomy, fusion), tumour, vascular, functional neurosurgery — each ranks independently',
      'Case studies with imaging (with consent) that referring doctors forward to patients',
      'Pre-op and post-op protocol pages your patients actually read — reduces phone queries',
    ],
  },
  oncology: {
    hook: 'When a Patient Hears \'Cancer\', They Should Reach an Oncologist First — Not a Hospital Brochure.',
    pain_points: [
      'Newly-diagnosed cancer patients Google symptoms and treatment options at 2 AM — they reach foreign hospital marketing pages before the oncologist who can actually evaluate their case and route them to the right tertiary centre.',
      'Medical, surgical, and radiation oncology all get bucketed into one listing on aggregators — patients can\'t find the right sub-specialist.',
      'Families looking for second opinions often can\'t even find a way to reach the oncologist directly — the conversation is mediated by a hospital call centre.',
    ],
    wins: [
      'Clear sub-specialty pages (medical, surgical, radiation, paediatric, gynae-onc, uro-onc)',
      'Compassionate patient-resource pages: \'what to expect\', \'nutrition during chemo\', \'second-opinion process\'',
      'Direct WhatsApp/email for second-opinion inquiries — families get a real person, not a call centre',
    ],
  },
  pulmonology: {
    hook: 'Asthma, COPD, Sleep Apnoea — Rank for the Conditions You Actually Treat.',
    pain_points: [
      'Patients with chronic cough or breathlessness Google their symptoms and see pharma ads for inhalers — not the pulmonologist who can diagnose them.',
      'Sleep clinics and bronchoscopy services can\'t be showcased in a directory tile.',
      'Post-COVID rehab, PAH, ILD are high-intent searches — they don\'t find you on aggregators.',
    ],
    wins: [
      'Per-condition pages: asthma, COPD, OSA, ILD, bronchiectasis, post-COVID lung rehab',
      'Spirometry, bronchoscopy, polysomnography service pages with what-to-expect',
      'Direct WhatsApp for inhaler-technique doubts — patients engage more, adherence goes up',
    ],
  },
  nephrology: {
    hook: 'Kidney Care Needs Continuity. DocSite Is That Continuity.',
    pain_points: [
      'CKD patients need long-term follow-up — but if your only web presence is a directory, they can\'t easily save your contact.',
      'Dialysis, transplant, and conservative-care pathways all look the same on a listing page.',
      'Referring GPs and diabetologists can\'t share a link to your \'CKD referral\' protocol — because you don\'t have one online.',
    ],
    wins: [
      'Per-service pages: CKD management, haemodialysis, peritoneal dialysis, transplant follow-up',
      'Patient-education pages on salt/protein intake, BP control, phosphate binders — patients actually read them',
      'Shareable referral-protocol URLs for your referring network — every click is a backlink over time',
    ],
  },
  rheumatology: {
    hook: 'Autoimmune Disease Is Invisible to Search. Make Yourself Visible.',
    pain_points: [
      'RA, lupus, spondylitis patients often take 2+ years to get diagnosed — pharma blogs rank for their early-symptom searches, not rheumatologists.',
      'Biologics, joint injections, and infusion-centre services can\'t be shown on a directory.',
      'You lose patients who find an \'autoimmune specialist\' through aggregators that route them to anyone with a medicine degree.',
    ],
    wins: [
      'Condition pages: RA, lupus, AS, PsA, Sjögren\'s, vasculitis, gout',
      'Infusion-centre and biologics service pages — answer \'is it safe?\' before patients call',
      'Pre-consult questionnaire on your site — saves 10 minutes per first visit',
    ],
  },
  endocrinology: {
    hook: 'Diabetes, Thyroid, PCOS — Long-Term Care Deserves a Long-Term Patient Relationship.',
    pain_points: [
      'Diabetes patients see pharma and aggregator content for every search — never the endocrinologist who can actually manage them.',
      'Thyroid, PCOS, pituitary, adrenal — all get merged into \'endocrine\' on directories.',
      'Existing patients can\'t easily access your reference-ranges or follow-up protocols.',
    ],
    wins: [
      'Condition pages: Type 1 & 2 diabetes, thyroid disorders, PCOS, osteoporosis, pituitary/adrenal',
      'Insulin-technique videos and sick-day rules — patients return to your site, not WhatsApp-forwards',
      'CGM and insulin-pump service pages — differentiates you from generic physicians',
    ],
  },
  allergy_immunology: {
    hook: 'Allergies Are Exploding — But Qualified Allergists Are Invisible Online.',
    pain_points: [
      'Patients searching \'food allergy specialist in {city}\' find generic physicians, not allergists with skin-prick testing or immunotherapy.',
      'Peanut, dust mite, drug allergy cases all go to pharma blogs because specialist content is absent.',
      'Immunotherapy (SIT, SLIT) is life-changing but patients have no idea it exists — your aggregator profile can\'t educate them.',
    ],
    wins: [
      'Service pages for skin-prick testing, specific IgE, immunotherapy, drug challenges, anaphylaxis protocols',
      'Condition pages: food allergy, allergic rhinitis, asthma-allergy, eczema, urticaria',
      'Pre-test preparation instructions patients read at home — reduces same-day cancellations',
    ],
  },
  radiology: {
    hook: 'Referring Doctors Choose You Based on Reports. Make Yours Findable.',
    pain_points: [
      'Diagnostic radiology is a B2B-to-doctor relationship, but referrers can\'t easily share your sub-specialty focus.',
      'Interventional radiology (IR) — biopsies, drainages, embolisations — is high-value but invisible to patient searches.',
      'Your centre\'s turnaround-time SLA, MRI protocols, and sub-specialty reads can\'t be showcased on a directory.',
    ],
    wins: [
      'Sub-specialty pages: neuroradiology, musculoskeletal, abdominal, breast, IR',
      'Referrer-focused pages: sample reports, TAT commitments, protocols for common indications',
      'Patient preparation pages (fasting for CT, metal-check for MRI) — reduces scan reschedules',
    ],
  },
  anesthesiology: {
    hook: 'Patients Fear Anaesthesia More Than Surgery. Own the Education.',
    pain_points: [
      'Patients undergoing surgery Google \'is anaesthesia safe?\' at 2 AM — they never find the anaesthesiologist doing their case.',
      'Pain clinics, pre-op clinics, and ICU-anaesthesia services get buried in aggregators.',
      'Surgeons who refer ASA-3/4 patients to you can\'t easily share a page that explains your high-risk expertise.',
    ],
    wins: [
      'Condition/service pages: pre-op assessment, regional anaesthesia, paediatric, obstetric, cardiac anaesthesia',
      'Pain-clinic pages: chronic pain, cancer pain, interventional procedures (nerve blocks, RFA)',
      'Patient-friendly \'what happens before anaesthesia\' page — reduces cancellations and anxiety',
    ],
  },
  internal_medicine: {
    // Legacy slug — actual copy lives under `physician` after the MD Medicine merge.
    // Kept here so any deep-link fallback doesn't crash; the route layer 301-redirects to /for/physician.
    hook: 'The Physician Patients Reach First — Then You Decide Which Specialist or Hospital They Need.',
    pain_points: [],
    wins: [],
  },
  general_medicine: {
    // Legacy slug — see note above. Redirects to /for/physician.
    hook: 'The Physician Patients Reach First — Then You Decide Which Specialist or Hospital They Need.',
    pain_points: [],
    wins: [],
  },
  physiotherapy: {
    hook: 'Recovery Happens at Home. So Should Your Patient\'s First Touchpoint.',
    pain_points: [
      'Post-surgery patients Google \'physio near me\' — they land on aggregators showing 100 profiles with no quality signal.',
      'Sports injuries, paediatric physio, neuro rehab, women\'s health — each deserves its own page.',
      'Home-visit and tele-rehab services can\'t be surfaced inside a directory tile.',
    ],
    wins: [
      'Per-condition pages: back pain, post-TKR rehab, stroke rehab, sports injury, paediatric, vestibular',
      'Exercise video library — patients return, share with family, stay engaged',
      'Home-visit booking CTA with pin-code range — specificity that aggregators can\'t match',
    ],
  },
  diabetology: {
    hook: 'Diabetes Care Is a Life-Long Partnership. Build It on Your Own Turf.',
    pain_points: [
      'Newly-diagnosed diabetics search \'diabetes doctor near me\' and find a mix of physicians, endocrinologists, and quacks on aggregators.',
      'Insulin technique, CGM, pump therapy, foot care — each is a dedicated page patients need.',
      'Existing patients can\'t easily access your reference-ranges or sick-day rules — WhatsApp forwards take over.',
    ],
    wins: [
      'Per-service pages: CGM, insulin pumps, diabetic foot, gestational diabetes, paediatric diabetes',
      'Patient-education library: sick-day rules, carb counting, hypoglycaemia — patients return repeatedly',
      'Direct WhatsApp for glucose-value queries — differentiates you from a 10-min aggregator slot',
    ],
  },
  infectious_disease: {
    hook: 'ID Consults Are Rare and High-Stakes. Be Findable When It Matters.',
    pain_points: [
      'Patients with complex infections (PUO, HIV, TB, post-transplant, tropical) struggle to find ID specialists — GPs don\'t know who to refer to.',
      'Travel-medicine, pre-travel vaccinations, and post-exposure prophylaxis are high-intent but invisible online.',
      'Hospital infection-control consulting is a B2B service that directories can\'t represent.',
    ],
    wins: [
      'Condition pages: HIV management, TB, hepatitis, tropical infections, bone/joint infections',
      'Travel-medicine page with destination-specific advice',
      'Referrer-focused page for hospitals: antibiotic stewardship, sepsis, transplant-ID consults',
    ],
  },
  vascular_surgery: {
    hook: 'Varicose Veins to Aortic Aneurysms — One Site, Every Procedure.',
    pain_points: [
      'Patients with leg swelling, varicose veins, or non-healing ulcers Google symptoms — they see pharma ads, not vascular surgeons.',
      'EVLA, EVAR, carotid endarterectomy, AV-fistula — each deserves a dedicated page, not a shared listing.',
      'Diabetic-foot and limb-salvage cases go to general surgeons because vascular specialists aren\'t discoverable.',
    ],
    wins: [
      'Procedure pages: varicose vein treatment (EVLA, RFA, foam), EVAR, carotid, AV fistula, limb salvage',
      'Condition pages: DVT, PAD, lymphoedema, diabetic foot',
      'Pre-op and post-op imaging protocols — reassures referring doctors and patients',
    ],
  },
  pain_management: {
    hook: 'Chronic Pain Is the Most Googled Medical Topic. Own the Answers.',
    pain_points: [
      'Chronic pain patients search for hours and land on alternative-medicine ads — they never find an interventional pain specialist.',
      'Nerve blocks, RFA, spinal cord stimulation are life-changing but invisible in aggregator listings.',
      'Cancer-pain and palliative-care needs are urgent and sensitive — directories are not the right first touch.',
    ],
    wins: [
      'Procedure pages: epidural, facet blocks, RFA, sympathetic blocks, SCS, intrathecal pumps',
      'Condition pages: low back pain, cervicogenic headache, CRPS, cancer pain, neuropathic pain',
      'Direct WhatsApp for cancer-pain queries — a compassionate response wins trust instantly',
    ],
  },
};

export function getSpecialtyCopy(slug) {
  const specName = SPEC_MAP[slug];
  if (!specName) return null;
  const copy = SPECIALTY_LANDING_COPY[slug] || {
    hook: `Your ${specName} Practice. Your Patient. Your Brand.`,
    pain_points: [
      `Patients searching '${specName.toLowerCase()} near me' reach aggregators and hospital marketing pages first — the specialist who'll actually treat them is invisible until later in the journey.`,
      'Directory listings treat you the same as every other doctor in your city — no clinical reasoning, no sub-specialty depth, no patient relationship.',
      'You can\'t tell your story, showcase your approach, or own the patient conversation when it lives on a third-party site.',
    ],
    wins: [
      `DocSite gives you a professional ${specName.toLowerCase()} practice surface in under 10 minutes — a platform, not just a website`,
      'AI-generated procedure pages that Google ranks — one page per treatment you actually offer',
      '0% commission on bookings — patients reach you directly, the conversation stays specialist-led',
    ],
  };
  return { specialty_slug: slug, specialty_name: specName, ...copy };
}


// ============================================================================
// COMPETITOR COMPARISONS (anonymised)
// ============================================================================
export const COMPETITOR_DATA = {
  'co-a': {
    name: 'Co. A (India\'s leading doctor directory)',
    type: 'Aggregator listing platform',
    summary: 'A well-known Indian patient-facing aggregator that lists thousands of doctors side-by-side. Patients discover you — but they land on the aggregator\'s site, not yours. You get a profile, not a website.',
    positioning: [
      { dim: 'Who owns the web presence?', us: 'You. A fully branded website at your own slug (or custom domain).', them: 'The aggregator. You\'re one of thousands of profiles on their site.' },
      { dim: 'Who owns patient data?', us: 'You. Your bookings, reviews, and patient contacts stay with you.', them: 'The aggregator. Data stays on their platform.' },
      { dim: 'Commission per booking', us: '0%.', them: 'Variable; higher visibility typically requires paid premium tiers.' },
      { dim: 'Showcase your procedures', us: 'Unlimited AI-generated per-procedure pages — each ranks independently on Google.', them: 'Limited profile fields within a shared listing template.' },
      { dim: 'SEO juice', us: 'Your slug accumulates backlinks over time — your domain authority grows.', them: 'All SEO flows to the aggregator\'s domain.' },
      { dim: 'Search by your name', us: 'Patients land on your site directly.', them: 'Patients land on the aggregator, see your profile alongside 10+ competitors.' },
    ],
    when_to_pick_us: 'You want a long-term career asset: your own website, your own brand, your own patient relationship. Aggregator listings can complement this; they shouldn\'t replace it.',
    when_to_pick_them: 'You want quick discovery volume and don\'t mind being one of many listings competing for the same patient.',
  },
  'co-b': {
    name: 'Co. B (India\'s leading Q&A + listing platform)',
    type: 'Aggregator + patient Q&A forum',
    summary: 'A major Indian platform that combines doctor listings with a patient-facing Q&A community. Doctors answer public questions; the platform owns all of that content.',
    positioning: [
      { dim: 'Primary surface', us: 'Your own standalone website.', them: 'Your profile on an aggregator domain.' },
      { dim: 'Content ownership', us: 'Articles, case studies, procedure pages — all live on your slug.', them: 'Your Q&A answers live on their domain; they own the URL and the SEO value.' },
      { dim: 'Commission', us: '0% on bookings.', them: 'Platform fees and visibility boosts vary.' },
      { dim: 'AI-generated SEO content', us: 'Monthly auto-generated articles + per-procedure pages, you approve before publish.', them: 'Manual posting required.' },
      { dim: 'Direct patient contact', us: 'WhatsApp CTA routes straight to your phone.', them: 'Mediated through their in-app messaging layer.' },
      { dim: 'Long-term value', us: 'Each piece of content compounds on your domain for years.', them: 'Your Q&A answers become the platform\'s asset, not yours.' },
    ],
    when_to_pick_us: 'You want content you own, SEO that compounds on your own domain, and direct patient relationships.',
    when_to_pick_them: 'You want to participate in a large public Q&A community alongside a listing.',
  },
  'co-c': {
    name: 'Co. C (India\'s pharmacy + tele-consult platform)',
    type: 'Pharmacy-first platform with doctor consults',
    summary: 'A major Indian online pharmacy that added doctor consults as a secondary feature. The consult experience is primarily tele-consultation bundled with pharmacy fulfillment.',
    positioning: [
      { dim: 'Core business', us: 'Doctor websites — that\'s all we do.', them: 'Pharmacy first; doctor consults are a secondary add-on.' },
      { dim: 'Your website', us: 'Yes — full site with bookings, procedures, case studies.', them: 'A listing card inside a pharmacy app.' },
      { dim: 'Consult type', us: 'In-person, video, or WhatsApp — your choice per slot.', them: 'Tele-consult focused.' },
      { dim: 'Data ownership', us: 'Your patients\' data is yours.', them: 'Shared across the pharmacy ecosystem (labs, medicine orders, etc.).' },
      { dim: 'Economics', us: 'Flat Rs. 0/299/999 monthly — no per-consult deduction.', them: 'Per-consult revenue share + platform fees.' },
      { dim: 'SEO juice', us: 'All backlinks and Google authority flow to your slug.', them: 'All SEO flows to the pharmacy\'s domain.' },
    ],
    when_to_pick_us: 'Your practice is in-person or hybrid, and you want a real website — not just a tele-consult slot in a pharmacy app.',
    when_to_pick_them: 'You exclusively offer tele-consults and want bundled pharmacy fulfillment.',
  },
  'co-d': {
    name: 'Co. D (India\'s surgery-focused aggregator)',
    type: 'Surgery-focused aggregator + hospital network',
    summary: 'A surgery-focused Indian platform that partners with hospitals and surgeons. Patients contact the platform; the platform triages and routes cases to the network.',
    positioning: [
      { dim: 'Who does the patient call?', us: 'You directly. WhatsApp/phone goes straight to your clinic.', them: 'The platform\'s call center. They triage and route.' },
      { dim: 'Whose brand wins?', us: 'Yours. Your name, your content, your site.', them: 'The platform\'s brand. You\'re the fulfillment partner.' },
      { dim: 'Economics', us: 'Flat Rs. 0/299/999 monthly — no per-case deduction.', them: 'Per-case revenue share; terms vary.' },
      { dim: 'Surgical portfolio display', us: 'Unlimited procedure pages, patient stories, before/after galleries on YOUR slug.', them: 'Your work appears under the platform\'s procedure pages, not yours.' },
      { dim: 'Long-term asset', us: 'Your website, your SEO, your patient list — compound value over 5–10 years.', them: 'You\'re part of their network as long as you\'re active with them.' },
      { dim: 'Lead attribution', us: 'Every lead credits you and lives in your dashboard.', them: 'Leads are the platform\'s; they attribute back to you on a case-by-case basis.' },
    ],
    when_to_pick_us: 'You want to build your own name and own patient pipeline — a career asset, not a referral arrangement.',
    when_to_pick_them: 'You want guaranteed case volume through a surgery-focused aggregator and are comfortable sharing revenue per case.',
  },
  'co-e': {
    name: 'Co. E (India\'s leading local-search directory)',
    type: 'General local-business directory (with a medical category)',
    summary: 'A huge general-purpose Indian local-business directory. Doctors are one of many business categories; the platform is optimised for phone-number discovery and paid placements, not medical content.',
    positioning: [
      { dim: 'Surface', us: 'A full medical website dedicated to you.', them: 'A listing card inside a general business directory.' },
      { dim: 'Content depth', us: 'Per-procedure pages, case studies, AI articles, credentials with NMC verification.', them: 'Business-card-style fields: name, number, category, address.' },
      { dim: 'Trust signals', us: 'Schema.org Physician markup + self-declared NMC registration number + patient case studies on every page.', them: 'Paid placement + user-submitted reviews alongside other businesses.' },
      { dim: 'SEO targeting', us: 'Ranks for clinical queries (\'FESS surgery in {city}\', \'best {specialty} for {condition}\').', them: 'Ranks for generic queries (\'doctors near me\') where you compete with every listing.' },
      { dim: 'Economics', us: 'Flat Rs. 0/299/999 monthly — nothing else.', them: 'Free listing + paid visibility tiers (verified badge, top placement, ads).' },
      { dim: 'What patient sees on call', us: 'Your clinic\'s phone, your staff, your brand.', them: 'The directory\'s call-tracking number first, routed to you.' },
    ],
    when_to_pick_us: 'You want a real medical website that ranks for clinical queries and builds your professional brand.',
    when_to_pick_them: 'You want a cheap phone-number-discovery listing alongside plumbers, caterers, and electricians in the same directory.',
  },
};

const LEGACY_SLUG_REDIRECTS = {
  practo: 'co-a',
  lybrate: 'co-b',
  '1mg': 'co-c',
  'pristyn-care': 'co-d',
  justdial: 'co-e',
};

export function getCompetitor(slug) {
  const s = (slug || '').toLowerCase().trim();
  const actualSlug = LEGACY_SLUG_REDIRECTS[s] || s;
  const data = COMPETITOR_DATA[actualSlug];
  if (!data) return null;
  return {
    competitor_slug: actualSlug,
    ...data,
    disclaimer: 'This comparison is a neutral, category-level overview of common platform archetypes in the Indian healthcare market. We do not name specific companies or practitioners. Observations are drawn from publicly available pricing, terms, and product pages. If any point is inaccurate, please write to contact@docsite.care and we will update within 48 hours.',
  };
}
